import { ChangeDetectionStrategy, Component, Input, OnInit } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, UntypedFormControl } from "@angular/forms";
import { isEmpty } from "lodash";

import { ContainerRatingsService } from "src/app/cbta-web/ratings/container-ratings-form/services/base/container-ratings.services";
import { CONSTANT } from "src/app/constant";
import { CandidateRole } from "src/app/models/enum";
import {
	DraftItem,
	InstructorOtherComments,
	InstructorOtherCommentsReg,
	OBComments,
	OBCommentsReq,
} from "src/app/models/models";

@Component({
	selector: "app-cbta-form-summary-modal",
	templateUrl: "./cbta-form-summary-modal.component.html",
	styleUrls: ["./cbta-form-summary-modal.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CbtaFormSummaryModalComponent implements OnInit {
	@Input() comments: OBComments[] = [];
	@Input() instructorComments: InstructorOtherComments[] = [];
	@Input() traineeAssessmentId: string = "";

	CANDIDATE_ROLE = CONSTANT.CANDIDATE_ROLE;

	editable: Array<boolean> = [];
	addObComments = new UntypedFormControl("");
	otherInstructorComments = new UntypedFormControl("");
	PFPMToggleForm!: FormGroup;

	constructor(
		private fb: FormBuilder,
		private containerRatingsService: ContainerRatingsService,
	) {}

	ngOnInit(): void {
		this.resetEditable();
		this.createPFPMToggleForm();
	}

	// ---------------- PF/PM toggle (logic unchanged) ----------------
	createPFPMToggleForm(): void {
		this.PFPMToggleForm = new FormGroup({
			PFPMToggles: this.fb.array([]),
		});
		this.updatePFPMToggles();
	}

	updatePFPMToggles(): void {
		this.comments.forEach((item, index) => {
			this.addPFPMToggle(index, item.candidateRole !== CONSTANT.CANDIDATE_ROLE.PF);
		});
	}

	addPFPMToggle(id: number, initialValue: boolean): void {
		this.toggleArray.push(
			this.fb.group({
				id: id,
				value: initialValue,
			}),
		);
	}

	getToggleControl(idx: number): FormGroup | null {
		const toggles = this.toggleArray.controls as FormGroup[];
		const toggleControl = toggles.find((toggle) => toggle.get("id")?.value === idx);
		return toggleControl || null;
	}

	getToggleValue(id: number): boolean {
		const control = this.getToggleControl(id);
		return control ? control.get("value")?.value : false;
	}

	get toggleArray(): FormArray {
		return this.PFPMToggleForm.get("PFPMToggles") as FormArray;
	}

	onToggleChange(id: number, isChecked: boolean): void {
		const control = this.getToggleControl(id);
		if (control) control.get("value")?.setValue(isChecked);
	}

	// ---------------- OB comments (logic unchanged) ----------------
	editComment(comment: OBComments, index: number): void {
		// remove previous open comments to non editable
		this.resetEditable();
		// open comments
		this.editable[index] = true;
		this.addObComments.setValue(comment.instructorComment);
	}

	saveComment(comment: OBComments, index: number): void {
		const record: DraftItem | undefined = this.containerRatingsService.draft;
		if (!record) return;
		this.setEditedOBComments("offline", comment, index, record);
	}

	setEditedOBComments(state: string, comment: OBComments, index: number, record: DraftItem): void {
		const candidateRating = record?.candidateRating ?? {};
		const addRemoveOb = candidateRating?.addRemoveOb ?? [];
		const selectedOBs = candidateRating?.selectedOBs ?? [];

		// NOTE: keep your original logic here (role = NONE)
		const role = CandidateRole.NONE;

		if (comment?.ob !== "TEM") {
			selectedOBs.map((item: OBComments) => {
				if (item.ob == comment.ob && item.candidateId == comment.candidateId && item.commentId == comment.commentId) {
					item.instructorComment = this.addObComments.value;
					this.comments[index].instructorComment = this.addObComments.value;
					this.comments[index].candidateRole = role;
					if (selectedOBs[index]) {
						selectedOBs[index].candidateRole = role; // keep original indexing behavior
					}
				}
			});
		}
		if (comment?.ob === "TEM") {
			selectedOBs.map((item: OBComments) => {
				if (item.ob == comment.ob && item.candidateId == comment.candidateId) {
					item.instructorComment = this.addObComments.value;
					this.comments[index].instructorComment = this.addObComments.value;
					this.comments[index].candidateRole = role;
					if (selectedOBs[index]) {
						selectedOBs[index].candidateRole = role; // keep original indexing behavior
					}
				}
			});
		}

		addRemoveOb.map((item: OBCommentsReq) => {
			if (
				item.maneuverCode == comment.maneuverCode &&
				item.candidateId == comment.candidateId &&
				item.observationCode == comment.observationCode
			) {
				item.description = this.addObComments.value;
				item.candidateRole = role;
			}
		});

		// Persist via ContainerRatingsService (same shape as your original DB update)
		this.containerRatingsService.updateSelectedDraft({
			candidateRating: {
				...candidateRating,
				selectedOBs,
				addRemoveOb,
			},
			lastUpdatedDate: new Date().toISOString(),
			continuePage: 2,
		});

		this.editable[index] = false;
		// update UI to set on/off toggle
		this.updatePFPMToggles();
	}

	// -------- Instructor "Other Training & Observations" (logic unchanged) --------
	editInstructorComment(comment: InstructorOtherComments, index: number): void {
		this.resetInstructorEditable();
		this.editable[index] = true;
		this.otherInstructorComments.setValue(comment.comment);
	}

	saveInstructorComment(comment: InstructorOtherComments, index: number): void {
		const record: DraftItem | undefined = this.containerRatingsService.draft;
		if (!record) return;
		this.setEditedOtherComments("offline", comment, index, record);
	}

	setEditedOtherComments(state: string, comment: InstructorOtherComments, index: number, record: DraftItem): void {
		const candidateRating = record?.candidateRating ?? {};
		const otherComments = candidateRating?.otherComments ?? [];

		otherComments.map((item: InstructorOtherCommentsReg) => {
			if (item.candidateId === comment.candidateId) {
				comment.comment = this.otherInstructorComments.value;
				item.comment = this.otherInstructorComments.value;
				// NOTE (kept): if multiple entries share candidateId, they all get overwritten
			}
		});

		this.containerRatingsService.updateSelectedDraft({
			candidateRating: {
				...candidateRating,
				otherComments,
			},
			lastUpdatedDate: new Date().toISOString(),
			continuePage: 2,
		});

		this.editable[index] = false;
	}

	// ---------------- helpers (logic unchanged) ----------------
	resetEditable(): void {
		this.editable = Array(this.comments.length).fill(false);
	}

	resetInstructorEditable(): void {
		this.editable = Array(this.instructorComments.length).fill(false);
	}

	isCommentEmpty(): boolean {
		return isEmpty(this.addObComments.value.replace(/\s/g, "")) ? true : false;
	}

	get inDraftMode(): boolean {
		return this.containerRatingsService.inDraftMode;
	}
}
